// =====================================================================
// iSentinel360 v0.4.1 — useConnectivity hook (v4 — navigator.onLine ONLY)
//
// === v9.4 still always reported offline. v9.5 removes the bridge. ===
//
// Both v9 and v9.4 had a bridge check. The bridge status was
// reading something that made the hook always say offline on
// the user's Android Studio WebView. We tried:
//   - v9: real fetch as tiebreaker (broke — always failed)
//   - v9.4: trust bridge 'offline' status (broke — bridge was
//     always reporting offline even when the user was online)
//
// v9.5 removes the bridge check entirely. The hook now ONLY
// trusts `navigator.onLine`. No bridge, no fetch, no clever
// logic. Just the OS-level signal.
//
//   navigator.onLine === true   →  online
//   navigator.onLine === false  →  offline
//
// This is intentionally the dumbest possible logic. It will be
// wrong in one specific case: the user is on a captive portal
// (hotel/airport WiFi that requires login) and `navigator.onLine`
// returns true even though they can't actually reach anything.
// But the false-positive (saying online when offline) is much
// less harmful than the false-negative (saying offline when
// online) — the user just sees a slightly stale UI instead of
// the app being completely bricked.
// =====================================================================

import { useEffect, useState } from 'react';

export type ConnectivitySource = 'navigator' | 'initial';

export interface ConnectivityState {
  isOnline: boolean;
  isChecking: boolean;
  source: ConnectivitySource;
}

const POLL_INTERVAL_MS = 7000;

export function useConnectivity(): ConnectivityState {
  const [state, setState] = useState<ConnectivityState>(() => ({
    isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
    isChecking: true,
    source: 'initial',
  }));

  useEffect(() => {
    const check = () => {
      const online =
        typeof navigator !== 'undefined' ? navigator.onLine : true;
      console.log(`[useConnectivity] navigator.onLine = ${online}`);
      setState({ isOnline: online, isChecking: false, source: 'navigator' });
    };

    check();

    const handleOnline = () => {
      console.log('[useConnectivity] window "online" event fired');
      check();
    };
    const handleOffline = () => {
      console.log('[useConnectivity] window "offline" event fired');
      check();
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    const pollId = window.setInterval(check, POLL_INTERVAL_MS);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.clearInterval(pollId);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return state;
}
